import React, { useState, useEffect } from 'react';
import {
  Badge,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  Divider,
  Chip,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { format } from 'date-fns';

export interface NotificationItem {
  id: string;
  type: 'CREATED' | 'CANCELLED' | 'CONFIRMED';
  message: string;
  timestamp: Date;
  read: boolean;
  bookingId?: string;
}

const NotificationButton: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  // Load notifications from localStorage on mount
  useEffect(() => {
    const loadNotifications = () => {
      const storedNotifications = localStorage.getItem('notifications');
      if (storedNotifications) {
        try {
          const parsed = JSON.parse(storedNotifications);
          const notificationsWithDates = parsed.map((n: any) => ({
            ...n,
            timestamp: new Date(n.timestamp),
          }));
          setNotifications(notificationsWithDates);
        } catch (error) {
          console.error('Failed to parse notifications:', error);
        }
      }
    };

    loadNotifications();

    // Listen for new notifications
    const handleNotificationAdded = () => {
      loadNotifications();
    };

    window.addEventListener('notificationAdded', handleNotificationAdded);

    return () => {
      window.removeEventListener('notificationAdded', handleNotificationAdded);
    };
  }, []);

  // Save notifications to localStorage whenever they change
  useEffect(() => {
    if (notifications.length > 0) {
      localStorage.setItem('notifications', JSON.stringify(notifications));
    }
  }, [notifications]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    // Mark all as read when closing
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const handleClearAll = () => {
    setNotifications([]);
    localStorage.removeItem('notifications');
    setAnchorEl(null);
  };

  const unreadCount = notifications.filter((n) => !n.read).length;
  const open = Boolean(anchorEl);

  const getNotificationIcon = (type: NotificationItem['type']) => {
    switch (type) {
      case 'CREATED':
        return '🎉';
      case 'CANCELLED':
        return '❌';
      case 'CONFIRMED':
        return '✅';
      default:
        return '📌';
    }
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        sx={{ position: 'relative' }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ width: 350, maxHeight: 500 }}>
          <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Notifications
            </Typography>
            {notifications.length > 0 && (
              <Typography
                variant="caption"
                color="primary"
                sx={{ cursor: 'pointer' }}
                onClick={handleClearAll}
              >
                Clear All
              </Typography>
            )}
          </Box>
          <Divider />

          {notifications.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No notifications yet
              </Typography>
            </Box>
          ) : (
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {[...notifications]
                .sort(
                  (a, b) =>
                    new Date(b.timestamp).getTime() -
                    new Date(a.timestamp).getTime()
                )
                .map((notification) => (
                  <React.Fragment key={notification.id}>
                    <ListItem
                      sx={{
                        backgroundColor: notification.read
                          ? 'transparent'
                          : 'action.hover',
                        '&:hover': {
                          backgroundColor: 'action.selected',
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <span>
                              {getNotificationIcon(notification.type)}
                            </span>
                            <Typography variant="body2">
                              {notification.message}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {format(
                              new Date(notification.timestamp),
                              'MMM dd, yyyy HH:mm'
                            )}
                          </Typography>
                        }
                      />
                      {!notification.read && (
                        <Chip label="New" size="small" color="primary" />
                      )}
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationButton;

// Helper function to add notification (can be called from other components)
export const addNotification = (notification: Omit<NotificationItem, 'id'>) => {
  const storedNotifications = localStorage.getItem('notifications');
  let notifications: NotificationItem[] = [];

  if (storedNotifications) {
    try {
      notifications = JSON.parse(storedNotifications);
    } catch (error) {
      console.error('Failed to parse notifications:', error);
    }
  }

  const newNotification: NotificationItem = {
    ...notification,
    id: `${Date.now()}-${Math.random()}`,
  };

  notifications.push(newNotification);
  localStorage.setItem('notifications', JSON.stringify(notifications));

  // Trigger a custom event to update the notification button
  window.dispatchEvent(new Event('notificationAdded'));
};
